import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { toWeb48Countdown } from '../../web48Offer';

// web48_timer_banner variation: Oben's announcement bar (Figma 7TAnSLvfTSD9lBet5PotzY,
// components desktop.blue / mobile.blue + the "Black Friday announcement bar"
// dev-handoff sheet). Fixed bar like the control banner, with an in-flow
// spacer of the same height. Breakpoints from the sheet: >=1024 full row,
// 700-1023 row without the sale label, <700 three stacked lines.

const Spacer = styled.div<{ $height: number }>`
  height: ${({ $height }) => $height}px;
`;

const Bar = styled.div`
  box-sizing: border-box;
  position: fixed;
  left: 0;
  top: var(--referral-banner-height, 0px);
  width: 100%;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #0b6dff;
  border-bottom: 1px solid #0957cc;
  color: #ffffff;
  min-height: 56px;
  padding: 0 40px;

  @media (max-width: 699px) {
    min-height: 0;
    padding: 11px 16px;
  }
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 48px;

  @media (max-width: 699px) {
    flex-direction: column;
    gap: 6px;
  }
`;

const Label = styled.span`
  font-family: 'Lato', sans-serif;
  font-weight: 700;
  font-size: 12px;
  line-height: 1.2;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  white-space: nowrap;

  @media (max-width: 699px) {
    font-size: 10px;
    letter-spacing: 0.16em;
  }
`;

// The sale label: hidden 700-1023 together with its divider.
const SaleLabel = styled(Label)`
  @media (min-width: 700px) and (max-width: 1023px) {
    display: none;
  }
`;

const Divider = styled.span<{ $first?: boolean }>`
  width: 1px;
  height: 22px;
  background: #6fa8ff;
  flex-shrink: 0;

  @media (max-width: 699px) {
    display: none;
  }
  ${({ $first }) => ($first ? '@media (min-width: 700px) and (max-width: 1023px) { display: none; }' : '')}
`;

const Headline = styled.span`
  font-family: 'Oswald', sans-serif;
  font-weight: 500;
  font-size: 21px;
  line-height: 1;
  letter-spacing: 0.01em;
  text-transform: uppercase;
  white-space: nowrap;

  @media (max-width: 699px) {
    font-size: 18px;
  }
`;

const TimerGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;

  @media (max-width: 699px) {
    gap: 10px;
  }
`;

const EndsInLabel = styled(Label)`
  font-size: 11px;
  letter-spacing: 0.18em;

  @media (max-width: 699px) {
    font-size: 10px;
    letter-spacing: 0.16em;
  }
`;

const Tiles = styled.div`
  display: flex;
  gap: 6px;

  @media (max-width: 699px) {
    gap: 4px;
  }
`;

const Tile = styled.span`
  box-sizing: border-box;
  display: inline-flex;
  align-items: baseline;
  justify-content: center;
  width: 36px;
  height: 28px;
  padding-top: 4px;
  border-radius: 5px;
  background: #0850bd;
  border: 1px solid #0745a3;

  @media (max-width: 699px) {
    width: 32px;
    height: 22px;
    padding-top: 2px;
  }
`;

const Digits = styled.span`
  font-family: 'Oswald', sans-serif;
  font-weight: 500;
  font-size: 15px;
  line-height: 1.2;
  font-variant-numeric: tabular-nums;

  @media (max-width: 699px) {
    font-size: 13px;
  }
`;

const Unit = styled.span`
  font-family: 'Lato', sans-serif;
  font-weight: 700;
  font-size: 11px;
  line-height: 1;
  margin-left: 1px;
  text-transform: lowercase;

  @media (max-width: 699px) {
    font-size: 10px;
  }
`;

// Past the end date: "ENDS [TODAY]" in the timer's place, one wide tile.
const TodayTile = styled(Tile)`
  width: auto;
  padding: 4px 10px 0;

  @media (max-width: 699px) {
    width: auto;
    padding: 2px 8px 0;
  }
`;

interface FallSaleBannerProps {
  // ms epoch of the shared end date; no countdown when undefined.
  endsAt?: number;
  // Black Friday sale (bfSale.ts): its label and percent replace the fall
  // sale copy. Defaults are web48's "EXCLUSIVE FALL SALE" at 20%.
  label?: string;
  percent?: number;
}

export const FallSaleBanner = ({ endsAt, label, percent = 20 }: FallSaleBannerProps) => {
  const { t } = useTranslation();
  const barRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(0);
  const [now, setNow] = useState(() => Date.now());

  const isCounting = endsAt !== undefined && now < endsAt;
  const isEnded = endsAt !== undefined && !isCounting;

  useEffect(() => {
    if (!isCounting) return;
    const id = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(id);
  }, [isCounting]);

  useEffect(() => {
    const el = barRef.current;
    if (!el) return;
    const observer = new ResizeObserver(([entry]) => setHeight(entry.borderBoxSize?.[0]?.blockSize ?? el.offsetHeight));
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const c = toWeb48Countdown(endsAt !== undefined ? endsAt - now : 0);
  const pad = (n: number) => String(n).padStart(2, '0');
  const tiles: [number, string][] = [
    [c.days, t('results.banner.web48.unitD')],
    [c.hours, t('results.banner.web48.unitH')],
    [c.minutes, t('results.banner.web48.unitM')],
    [c.seconds, t('results.banner.web48.unitS')],
  ];

  return (
    <Spacer $height={height}>
      <Bar ref={barRef} data-testid="web48-variation-banner">
        <Row>
          <SaleLabel>{label ?? t('results.banner.web48.saleLabel')}</SaleLabel>
          <Divider $first />
          <Headline>{t('results.banner.web48.headline', { percent })}</Headline>
          {isCounting && (
            <>
              <Divider />
              <TimerGroup>
                <EndsInLabel>{t('results.banner.web48.endsIn')}</EndsInLabel>
                <Tiles
                  role="timer"
                  aria-label={t('results.banner.web48.timerLabel', { days: c.days, hours: c.hours, minutes: c.minutes })}
                  data-testid="web48-timer"
                >
                  {tiles.map(([value, unit]) => (
                    <Tile key={unit} aria-hidden="true">
                      <Digits>{pad(value)}</Digits>
                      <Unit>{unit}</Unit>
                    </Tile>
                  ))}
                </Tiles>
              </TimerGroup>
            </>
          )}
          {isEnded && (
            <>
              <Divider />
              <TimerGroup data-testid="web48-ends-today">
                <EndsInLabel>{t('results.banner.web48.endsLabel')}</EndsInLabel>
                <TodayTile>
                  <Digits>{t('results.banner.web48.today')}</Digits>
                </TodayTile>
              </TimerGroup>
            </>
          )}
        </Row>
      </Bar>
    </Spacer>
  );
};
